<template>
  <a-card class="identity-verify-form-card">
    <a-alert
      v-if="isRejected && verificationState.comments"
      type="error"
      show-icon
      :message="verificationState.comments"
      style="margin-bottom: 1.5rem"
    />
    <ValidationObserver ref="observer">
      <a-form
        :label-width="80"
        slot-scope="{ validate }"
        @submit.prevent="validate().then(handleSubmit)"
      >
        <h2 class="title">{{ labels.title }}</h2>
        <a-divider />

        <a-row :gutter="[16, 16]" type="flex" justify="start">
          <a-col :span="24">
            <h3 class="label">{{ labels.form.upload }}</h3>
            <p class="description">{{ labels.form.description }}</p>
          </a-col>
        </a-row>

        <a-row :gutter="[16, 16]" type="flex" justify="start">
          <a-col :span="24">
            <a-form-item :style="{ width: '100%' }">
              <image-capture
                @preview="handlePreviewFile"
                capture="environment"
                @change="(file) => onUpload(file, 'dfp')"
                @remove="() => handleRemoveFile('dfp')"
                :file-url="dfpURI"
              >
                <a-button
                  size="large"
                  type="primary"
                  ghost
                  class="btn-upload"
                  :disabled="submitting"
                  :block="true"
                  :style="{ width: '100%' }"
                >
                  <span>
                    <a-icon type="upload" />
                    {{ labels.form.dfp.label }}
                  </span>
                  <font-awesome-icon
                    :icon="['fa', 'id-card']"
                    class="anticon fai"
                    style="margin-left: 8px"
                  />
                </a-button>
              </image-capture>
            </a-form-item>
          </a-col>

          <a-col :span="24">
            <a-form-item :style="{ width: '100%' }">
              <image-capture
                @preview="handlePreviewFile"
                capture="environment"
                @change="(file) => onUpload(file, 'dbp')"
                @remove="() => handleRemoveFile('dbp')"
                :file-url="dbpURI"
              >
                <a-button
                  size="large"
                  type="primary"
                  ghost
                  class="btn-upload"
                  :disabled="submitting"
                  :block="true"
                  :style="{ width: '100%' }"
                >
                  <span>
                    <a-icon type="upload" />
                    {{ labels.form.dbp.label }}
                  </span>
                  <font-awesome-icon
                    :icon="['fa', 'id-card']"
                    class="anticon fai"
                    :style="{ transform: 'rotateY(180deg)' }"
                    style="margin-left: 8px"
                  />
                </a-button>
              </image-capture>
            </a-form-item>
          </a-col>

          <a-col :span="24">
            <a-form-item :style="{ width: '100%' }">
              <image-capture
                @preview="handlePreviewFile"
                capture="user"
                @change="(file) => onUpload(file, 'selfie')"
                @remove="() => handleRemoveFile('selfie')"
                :file-url="selfieURI"
              >
                <a-button
                  size="large"
                  type="primary"
                  ghost
                  class="btn-upload"
                  :disabled="submitting"
                  :block="true"
                  :style="{ width: '100%' }"
                >
                  <span>
                    <a-icon type="upload" />
                    {{ labels.form.selfie.label }}
                  </span>
                  <font-awesome-icon
                    :icon="['fa', 'image-portrait']"
                    class="anticon fai"
                    style="margin-left: 8px"
                  />
                </a-button>
              </image-capture>
            </a-form-item>
          </a-col>
        </a-row>

        <a-form-item style="text-align: right">
          <a-button
            type="default"
            html-type="submit"
            :loading="submitting"
            :disabled="submitting"
            size="large"
            class="btn-submit"
            @click.prevent="validate().then(handleSubmit)"
          >
            <span>{{ labels.form.submit }}</span>
          </a-button>
        </a-form-item>
      </a-form>
    </ValidationObserver>
  </a-card>
</template>
<script>
import { ValidationObserver } from "vee-validate";
import labels from "@/utils/labels";
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";
import ImageCapture from "@/components/ImageCapture.vue";
const initialState = {
  dfp: null,
  dbp: null,
  selfie: null,
};
export default {
  name: "IdentityVerifyForm",
  components: {
    ValidationObserver,
    ImageCapture,
  },
  data() {
    return {
      form: _.cloneDeep(initialState),
      submitting: false,
      labels: labels.profile.verifyIdentity,
      dfpURI: null,
      dbpURI: null,
      selfieURI: null,
    };
  },
  computed: {
    ...mapGetters({
      verificationState: "identityVerification/getVerificationState",
      loggedUser: "clientAuth/getLoggedUser",
    }),
    isRejected() {
      return (
        this.loggedUser &&
        this.verificationState &&
        this.verificationState.status === "rejected"
      );
    },
  },
  methods: {
    ...mapActions({
      requestIdentityVerify: "identityVerification/identityVerify",
      getIdentityVerifyState: "identityVerification/identityVerifyState",
    }),
    async toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
    },
    dummyRequest({ onSuccess }) {
      setTimeout(() => {
        onSuccess("ok");
      }, 0);
    },
    handlePreviewFile(file) {
      // CREATE IMG AND OPEN IN NEW TAB
      const img = document.createElement("img");
      img.src = file.url || file.thumbUrl;
      img.style.maxWidth = "520px";
      const w = window.open("about:blank", "_blank", "width=520,height=auto");
      w?.document.write(img.outerHTML);
    },
    handleRemoveFile(field) {
      if (field === "dfp") {
        this.dfpURI = null;
      } else if (field === "dbp") {
        this.dbpURI = null;
      } else if (field === "selfie") {
        this.selfieURI = null;
      }
      this.form[field] = null;
    },
    async onUpload(file, field) {
      const fileBase64 = await this.toBase64(file);
      this[`${field}URI`] = fileBase64;
      this.form[field] = file;
    },
    async handleSubmit() {
      // VALIDATE FILES
      const errors = [];
      if (!this.form.dfp) {
        errors.push(this.labels.form.dfp.required);
      }
      if (!this.form.dbp) {
        errors.push(this.labels.form.dbp.required);
      }
      if (!this.form.selfie) {
        errors.push(this.labels.form.selfie.required);
      }
      if (errors.length > 0) {
        this.$error({
          class: "modal-list-errors-wrapper",
          title: this.labels.error,
          content: (h) =>
            h("div", { class: "modal-list-errors-container" }, [
              h("p", this.labels.form.errorsTitle),
              h(
                "ul",
                { class: "modal-error-list" },
                errors.map((err) => h("li", err))
              ),
            ]),
          centered: true,
          closable: true,
          okText: this.labels.ok,
        });
        return;
      }
      try {
        this.submitting = true;
        const form = _.cloneDeep(this.form);
        const formData = new FormData();
        formData.append("dfp", form.dfp, form.dfp.name);
        formData.append("dbp", form.dbp, form.dbp.name);
        formData.append("selfie", form.selfie, form.selfie.name);
        await this.requestIdentityVerify(formData);
        this.$success({
          title: this.labels.form.success,
          centered: true,
          onOk: () => {
            this.form = _.cloneDeep(initialState);
            this.dfpURI = null;
            this.dbpURI = null;
            this.selfieURI = null;
            this.$refs.observer?.reset();
            this.$store.dispatch("clientAuth/checkSession");
            this.$store.dispatch("identityVerification/identityVerifyState");
            this.$router.push({ name: "home" });
          },
        });
      } catch (error) {
        this.$error({
          title: this.labels.error,
          content:
            error?.response?.data?.message ||
            error?.response?.message ||
            error.message,
          centered: true,
        });
      } finally {
        this.submitting = false;
      }
    },
    handleBack() {
      this.$router.back();
    },
  },
  watch: {
    verificationState: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          this.getIdentityVerifyState();
        } else if (
          this.loggedUser &&
          (this.loggedUser.ivp?.verified || this.loggedUser.ivp?.requested) &&
          !this.isRejected
        ) {
          this.$router.push({ name: "home" });
        }
      },
    },
    loggedUser: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (
          newVal &&
          newVal.ivp &&
          (newVal.ivp.verified || newVal.ivp.requested) &&
          this.verificationState &&
          !this.isRejected
        ) {
          this.$router.push({ name: "home" });
        }
      },
    },
  },
};
</script>
<style lang="scss">
@import "~@/assets/scss/variable";
.identity-verify-form-card {
  background-color: var(--card-shop-bg);
  border-color: var(--card-shop-shadow);
  padding: 3.125rem;
  border-radius: 8px;
  max-width: 540px;
  @media screen and (max-width: $screen-mobile) {
    padding: 2.125rem 1rem;
  }
  .title {
    font-family: "Exo 2", sans-serif;
    color: var(--text);
    font-weight: 400;
    margin: 0;
    line-height: 1.3;
    word-break: break-word;
    font-size: 2rem;
    margin-bottom: 1rem;
    text-align: center;
  }
  .ant-card-body {
    padding: 0;
  }
  .ant-form label {
    font-size: 0.875rem;
    color: var(--text);
    font-family: "Roboto", sans-serif;
  }
  .ant-input {
    background-color: var(--input-bg);
    height: 3.75rem;
    color: var(--text);
    &:active,
    &:focus,
    &:hover {
      border-color: var(--text-active);
    }
  }
  .ant-input-affix-wrapper {
    .ant-input:not(:first-child) {
      padding-left: 2.8125rem;
    }
    .ant-input-prefix {
      color: var(--text);
      font-size: 1.375rem;
      font-family: "Roboto", sans-serif;
    }
    .ant-input-clear-icon,
    .ant-input-password-icon,
    .ant-input-textarea-clear-icon {
      color: var(--text);
    }
  }

  .has-error .ant-input-affix-wrapper .ant-input,
  .has-error .ant-input-affix-wrapper .ant-input:hover {
    background-color: var(--input-bg);
  }

  .btn-submit {
    color: var(--btn-text-primary) !important;
    background-color: var(--btn-bg-primary);
    border-color: var(--btn-bc-primary);
    height: 51px;
    padding: 0 1.75rem;
    width: 100% !important;
  }

  .back-link {
    @extend .btn-submit;
    color: var(--text-active) !important;
  }

  .label,
  .description {
    color: var(--text);
    font-family: "Roboto", sans-serif;
  }
}

input:-internal-autofill-selected {
  background-color: var(--input-bg) !important;
  border-color: var(--text-active) !important;
  color: var(--text-muted) !important;
}
.upload-dfp,
.upload-dbp,
.upload-selfie {
  .ant-upload-select {
    display: block !important;
  }
}
button.btn-upload {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  color: var(--text) !important;
  border-color: var(--text) !important;
  .fai {
    font-size: 1.25rem;
    color: var(--btn-bg-primary) !important;
  }
}
.modal-list-errors-wrapper {
  .ant-modal-content {
    background-color: var(--modal-bg) !important;
    border-radius: 0;
    border: 1px solid var(--modal-shadow) !important;
    box-shadow: 0 1px 3px 3px var(--modal-shadow) !important;
    .ant-btn {
      color: var(--btn-text-secondary);
      border-color: var(--btn-bc-secondary);
      background-color: var(--btn-bg-secondary);
      &:hover,
      &:focus,
      &:active {
        color: var(--btn-text-secondary);
        border-color: var(--btn-bc-secondary);
        background-color: var(--btn-bg-secondary);
      }
      &.ant-btn-primary {
        color: var(--btn-text-primary);
        border-color: var(--btn-bc-primary);
        background-color: var(--btn-bg-primary);
        &:hover {
          color: var(--btn-text-primary);
          border-color: var(--btn-bc-primary);
          background-color: var(--btn-bg-primary);
        }
      }
    }
    .modal-list-errors-container {
      p {
        color: var(--text);
      }
      .modal-error-list {
        padding-left: 1.25rem;
        li {
          color: var(--danger);
          margin-bottom: 0.5rem;
        }
      }
    }
  }
  .ant-modal-header {
    background-color: transparent;
    border-color: var(--modal-shadow);
  }
  .ant-modal-close {
    color: var(--text-muted) !important;
    &:hover {
      color: var(--text) !important;
    }
  }
}
</style>
